import {
  createDefaultRoutineFormDraft,
  createMeetingTriggerDraft,
  createOutlookTriggerDraft,
  createRoutineFormDraftFromRoutine,
  DEFAULT_GMAIL_TRIGGER_LABELS,
  DEFAULT_ROUTINE_ICON,
  hasNoUpcomingIntervalOccurrence,
  HttpError,
  MEETING_TRIGGER_KINDS,
  ROUTINE_INTERVAL_END_ERROR,
  ROUTINE_TRIGGER_GMAIL,
  ROUTINE_TRIGGER_MEETING_STARTED,
  ROUTINE_TRIGGER_MEETING_SUMMARIZED,
  ROUTINE_TRIGGER_OUTLOOK,
  ROUTINE_TRIGGER_WEBHOOK,
  type Routine,
  type RoutineFormDraft,
  routineFormDraftsEqual,
  type RoutineLibraryItem,
  RoutineFormSubmissionSchema,
  type RoutineTriggerDraft,
  useStores,
  type WebhookEndpoint,
} from '@littlebird/sdk';
import {
  buildRoutineRecurrenceRule,
  buildRoutineUpsertInput,
  DEFAULT_ROUTINE_INTERVAL_COUNT,
  DEFAULT_ROUTINE_INTERVAL_UNIT,
  EVERY_MONTH,
  MONTH_DAYS,
  MONTH_LABELS,
  type RoutineActionState,
  type RoutineAgentMode,
  ROUTINE_FREQUENCY_OPTIONS,
  ROUTINE_INTERVAL_COUNTS,
  ROUTINE_INTERVAL_UNITS,
  routineIntervalCounts,
  type RoutineTriggerFrequency,
  syncAdvancedTime,
  toRoutineDateTimeValue,
  toRoutineScheduleParts,
  useInitialRoutineDraft,
  useRoutineFormDraft,
  WEEK_DAY_CODES,
  WEEK_DAYS,
  WEEKDAY_ONLY_CODES,
  WEEKDAYS_FREQUENCY,
} from '@littlebird/ui-utils';
import { marked } from 'marked';
import { observer } from 'mobx-react-lite';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { MicrophoneButton } from '@/components/blocks/Thread/MicrophoneButton';
import { type BaseModalFCProps } from '@/components/modals/modalsManager';
import { Input, Tooltip, XStack, YStack } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { MarkdownEditor } from '@/components/ui/editor/MarkdownEditor';
import { IconPickerTrigger } from '@/components/ui/icon-picker';
import { Label } from '@/components/ui/Label';
import { ScrollMask } from '@/components/ui/ScrollMask';
import { Markdown } from '@/components/ui/smoothdown';
import { Switch } from '@/components/ui/Switch';
import { TimePickerPopover } from '@/components/ui/TimePickerPopover';
import { FreeLimitExceededError } from '@/error';
import { FEATURES, useFeature } from '@/features/feature-flags';
import { useConnectedAccountsQuery } from '@/features/integrations/integrations.query';
import { DeliveryChannelSelect } from '@/features/msg-channels/common/DeliveryChannelSelect';
import {
  MESSAGING_DELIVERY_OFF,
  MessagingChannelTypes,
  WHATSAPP_REPLY_WINDOW_NOTE,
} from '@/features/msg-channels/common/msg-channels.types';
import { useResumeRoutine } from '@/features/routines/hooks/use-resume-routine';
import { getRoutineActions } from '@/features/routines/routine-action-state';
import { RoutineTeamShareRow } from '@/features/routines/routine-team-share';
import {
  type RoutineTriggerKindsEnabled,
  useAdvancedSchedulesEnabled,
  useRoutineTriggerKindsEnabled,
} from '@/features/routines/routines.hooks';
import { RecurrenceType } from '@/features/routines/routines.types';
import {
  useRoutineLibrary,
  useWebhookEndpoints,
} from '@/features/routines/use-routine-refresh';
import { openSettings } from '@/features/settings/settings-navigation';
import { useSpeechToText } from '@/features/speech-input/use-speech-to-text';
import {
  CalloutBox,
  RoutineCreditShareRow,
  subscriptionStore,
  useSubscription,
} from '@/features/subscription';
import { logError } from '@/lib/errorLogging';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';
import { navigate } from '@/utils/navigation-service';
import { assertNever } from '@/utils/types';

import { RestartWaitingRunNote } from './components/restart-waiting-run-note';
import {
  type GmailAccountOption,
  RoutineGmailTriggerRow,
} from './components/routine-gmail-trigger-row';
import { LibraryBrowser } from './components/routine-library-section';
import { RoutineMeetingTriggerRow } from './components/routine-meeting-trigger-row';
import { RoutineMemorySection } from './components/routine-memory-section';
import { RoutineOutlookTriggerRow } from './components/routine-outlook-trigger-row';
import { WebhookTriggerRow } from './components/webhook-trigger-row';
import {
  type EmailTriggerDraft,
  markSentToChat,
  startEmailTriggerChat,
} from './configure-email-trigger';
import { useRestartWaitingRun } from './hooks/use-restart-waiting-run';
import { useDeleteRoutine } from './hooks/use-routine-actions';
import { useWebhookEndpointActions } from './hooks/use-webhook-endpoint-actions';
import { onlyNotificationsChanged } from './routine-form-permissions';
import { MEETING_TRIGGER_COPY } from './routine-meeting-triggers';
import { templateSourceFor } from './routine-template-analytics';
import {
  getRoutineDraftOwnerId,
  type RoutineCreateFlowState,
  routinesStore,
} from './routines.state';

const INSTRUCTIONS_PREVIEW_BODY_TYPES = new Set([
  'paragraph',
  'blockquote',
  'list',
]);

const getInstructionsPreviewMarkdown = (markdown: string) => {
  const tokens = marked.lexer(markdown).filter(({ type }) => type !== 'space');
  const heading = tokens[0]?.type === 'heading' ? tokens[0] : undefined;
  const bodyStart = heading ? 1 : 0;
  const bodyIndex = tokens.findIndex(
    ({ type }, index) =>
      index >= bodyStart && INSTRUCTIONS_PREVIEW_BODY_TYPES.has(type)
  );
  const body = bodyIndex >= 0 ? tokens[bodyIndex] : undefined;
  const lastIncludedIndex = body ? bodyIndex : heading ? 0 : -1;
  const preview = [heading?.raw, body?.raw]
    .filter(Boolean)
    .join('\n')
    .trimEnd();

  if (!preview) return markdown;
  return tokens.length > lastIncludedIndex + 1
    ? `${preview.replace(/[.:;,!?]+$/, '')}...`
    : preview;
};

const AGENT_MODE_OPTIONS = [
  { value: 'lite', label: 'Quick' },
  { value: 'regular', label: 'Regular' },
  { value: 'max', label: 'Max' },
  { value: 'ultra', label: 'Ultra' },
  { value: 'god', label: 'God' },
] satisfies { value: RoutineAgentMode; label: string }[];

const INTERVAL_UNIT_LABELS: Record<RoutineFormDraft['intervalUnit'], string> = {
  minutes: 'minutes',
  hours: 'hours',
  days: 'days',
};

const GMAIL_TOOLKIT = 'gmail';
const OUTLOOK_TOOLKIT = 'outlook';

const CUSTOM_TEMPLATE_ID = 'custom';

type TemplateIcon = NonNullable<RoutineFormDraft['icon']>;

const DEFAULT_INTERVAL_COUNT_BY_UNIT: Record<
  RoutineFormDraft['intervalUnit'],
  number
> = { minutes: DEFAULT_ROUTINE_INTERVAL_COUNT, hours: 1, days: 1 };

const defaultIntervalEnd = (now = new Date()): string => {
  const end = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  return toRoutineDateTimeValue(end);
};

const TEMPLATE_ICONS: Record<string, TemplateIcon> = {
  daily_morning_brief: { type: 'icon', code: 'sparkles', color: 'amber' },
  end_of_day_digest: { type: 'icon', code: 'notepad', color: 'violet' },
  weekly_activity_summary: {
    type: 'icon',
    code: 'chart-bar',
    color: 'crimson',
  },
  monthly_review: { type: 'icon', code: 'calendar', color: 'blue' },
};

const TAB_ICONS: Record<string, TemplateIcon> = {
  calendar: { type: 'icon', code: 'calendar', color: 'blue' },
  inbox: { type: 'icon', code: 'sparkles', color: 'amber' },
  docs: { type: 'icon', code: 'notepad', color: 'violet' },
  code: { type: 'icon', code: 'code', color: 'green' },
};

const getTemplateIcon = (id: string, tags?: string[]): TemplateIcon => {
  const tab = tags?.find((tag) => tag.startsWith('tab:'))?.slice(4);
  return {
    ...(TEMPLATE_ICONS[id] ??
      (tab ? TAB_ICONS[tab] : undefined) ??
      DEFAULT_ROUTINE_ICON),
  };
};

type CreateRoutineModalProps = BaseModalFCProps & {
  routine?: Routine;
  closeOnNavigate?: boolean;
  libraryItem?: RoutineLibraryItem;
  isCustom?: boolean;
  onImproveRoutine?: () => void;
  improveRoutineDisabledReason?: string;
};

const makeDefaultDraft = createDefaultRoutineFormDraft;

const makeDraftFromRoutine = (routine: Routine): RoutineFormDraft =>
  createRoutineFormDraftFromRoutine(routine);

const makeDraftFromLibraryItem = (
  item: RoutineLibraryItem
): RoutineFormDraft => ({
  ...makeDefaultDraft(),
  ...(item.recurrence_rule
    ? createRoutineFormDraftFromRoutine({
        title: item.title,
        prompt: item.prompt,
        recurrenceRule: item.recurrence_rule,
        notificationsEnabled: true,
        emailNotificationsEnabled: true,
        agentMode: item.agent_mode,
        triggers: item.triggers,
      })
    : {
        title: item.title,
        prompt: item.prompt,
        agentMode: item.agent_mode ?? 'regular',
        triggers: item.triggers ?? [],
        scheduleEnabled: !item.triggers?.length,
      }),
  kind: item.kind,
  icon: item.icon ?? getTemplateIcon(item.id, item.tags),
  iconSuggestionsEnabled: !item.icon,
});

type UseRoutineCreateFlowStateOptions = {
  draftOwnerId: string;
  routine?: Routine;
  libraryItem?: RoutineLibraryItem;
  isCustom?: boolean;
  open: boolean;
};

type RoutineCreateFlowUpdates = Partial<
  Pick<RoutineCreateFlowState, 'step' | 'selectedTemplateId'>
>;

const useRoutineCreateFlowState = ({
  draftOwnerId,
  routine,
  libraryItem,
  isCustom,
  open,
}: UseRoutineCreateFlowStateOptions) => {
  const initialState = useMemo<RoutineCreateFlowState>(() => {
    if (routine) {
      return {
        sourceKey: `routine:${routine.referenceId}`,
        step: 'configure',
        selectedTemplateId: CUSTOM_TEMPLATE_ID,
      };
    }
    if (libraryItem) {
      return {
        sourceKey: `library:${libraryItem.id}`,
        step: 'configure',
        selectedTemplateId: libraryItem.id,
      };
    }
    if (isCustom) {
      return {
        sourceKey: 'custom',
        step: 'configure',
        selectedTemplateId: CUSTOM_TEMPLATE_ID,
      };
    }
    return {
      sourceKey: 'pick',
      step: 'pick',
      selectedTemplateId: CUSTOM_TEMPLATE_ID,
    };
  }, [isCustom, libraryItem, routine]);

  const storedState = routinesStore.useCreateFlowState(draftOwnerId);
  const state =
    storedState?.sourceKey === initialState.sourceKey
      ? storedState
      : initialState;
  const templateChanged =
    storedState !== null &&
    storedState.sourceKey !== initialState.sourceKey &&
    storedState.selectedTemplateId !== initialState.selectedTemplateId;

  useEffect(() => {
    if (open) {
      routinesStore.initializeCreateFlowState(draftOwnerId, initialState);
    }
  }, [draftOwnerId, initialState, open]);

  const update = useCallback(
    (updates: RoutineCreateFlowUpdates) => {
      routinesStore.updateCreateFlowState(draftOwnerId, initialState, updates);
    },
    [draftOwnerId, initialState]
  );

  const clear = useCallback(() => {
    routinesStore.clearCreateFlowState(draftOwnerId);
  }, [draftOwnerId]);

  return { ...state, templateChanged, update, clear };
};

export const CreateRoutineModal = observer(function CreateRoutineModal({
  routine,
  closeOnNavigate = true,
  libraryItem,
  isCustom,
  onImproveRoutine,
  improveRoutineDisabledReason,
  onOpenChange,
  open,
}: CreateRoutineModalProps) {
  const { reports, routineDrafts, routines } = useStores();
  const triggersEnabled = useRoutineTriggerKindsEnabled();
  const { data: gmailAccounts } = useConnectedAccountsQuery(GMAIL_TOOLKIT);
  const { data: outlookAccounts } = useConnectedAccountsQuery(OUTLOOK_TOOLKIT);
  const webhookEndpoints = useWebhookEndpoints(triggersEnabled.webhook);
  const isEditMode = !!routine;
  const actions = routine ? getRoutineActions(routine) : undefined;
  const notificationsOnly =
    isEditMode && !actions?.permissions.canEditDefinition;
  const advancedSchedulesEnabled = useAdvancedSchedulesEnabled();

  const { items: libraryItems } = useRoutineLibrary();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isShowingModalOnTop, setIsShowingModalOnTop] = useState(false);
  const [isEditingInstructions, setIsEditingInstructions] = useState(false);
  const [isIconPickerOpen, setIsIconPickerOpen] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const isEditingInstructionsRef = useRef(false);
  const [instructionsDraft, setInstructionsDraft] = useState('');
  const [instructionsSyncVersion, setInstructionsSyncVersion] = useState(0);
  const [findPortalContainer, setFindPortalContainer] =
    useState<HTMLDivElement | null>(null);

  const draftKey = routine?.referenceId ?? 'create';
  const draftOwnerId = getRoutineDraftOwnerId();
  const {
    sourceKey: createFlowSourceKey,
    templateChanged: createFlowTemplateChanged,
    step,
    selectedTemplateId,
    update: updateCreateFlowState,
    clear: clearCreateFlowState,
  } = useRoutineCreateFlowState({
    draftOwnerId,
    routine,
    libraryItem,
    isCustom,
    open,
  });
  const createInitialDraft = useCallback(
    () =>
      routine
        ? makeDraftFromRoutine(routine)
        : libraryItem
          ? makeDraftFromLibraryItem(libraryItem)
          : makeDefaultDraft(),
    [routine, libraryItem]
  );
  const initialDraft = useInitialRoutineDraft(open, createInitialDraft);
  const editBaseline = routine ? initialDraft : null;
  const { draft, updateDraft, clearDraft } = useRoutineFormDraft({
    routineDrafts,
    open,
    keepDraftOnClose: true,
    draftOwnerId,
    draftKey,
    initialDraft,
  });

  useEffect(() => {
    if (open && draftKey === 'create' && createFlowTemplateChanged) {
      routineDrafts.clearCustomBackup(draftOwnerId);
      routineDrafts.setDraft(draftOwnerId, draftKey, initialDraft);
    }
  }, [
    createFlowTemplateChanged,
    draftKey,
    draftOwnerId,
    initialDraft,
    open,
    routineDrafts,
  ]);
  const { prompt } = draft;
  const handleTranscript = useCallback((transcript: string) => {
    if (!isEditingInstructionsRef.current) return;
    setInstructionsDraft((current) =>
      current ? `${current} ${transcript}` : transcript
    );
    setInstructionsSyncVersion((current) => current + 1);
  }, []);
  const {
    recordingStatus,
    startRecording,
    stopRecording,
    cancelRecording,
    mediaStreamRef,
  } = useSpeechToText(handleTranscript);

  const resumeRoutine = useResumeRoutine();
  const { showModal: showDeleteRoutineModal } = useDeleteRoutine();
  const isPauseActionPending =
    routine !== undefined &&
    (routines.isOperationPending('pause', routine.referenceId) ||
      routines.isOperationPending('resume', routine.referenceId));
  const subscription = useSubscription();
  const exitInstructionsEditor = useCallback(() => {
    isEditingInstructionsRef.current = false;
    cancelRecording();
    setIsEditingInstructions(false);
  }, [cancelRecording]);

  useEffect(() => {
    setSubmitAttempted(false);
    setIsIconPickerOpen(false);
    exitInstructionsEditor();
    if (open) {
      setIsConfirmingDelete(false);
      setDraftRestored(routineDrafts.isDraftEdited(draftOwnerId, draftKey));
    }
  }, [
    createFlowSourceKey,
    draftKey,
    draftOwnerId,
    exitInstructionsEditor,
    open,
    routineDrafts,
  ]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean, force = false) => {
      if (!nextOpen && isSubmitting && !force) return;
      if (!nextOpen) {
        setIsIconPickerOpen(false);
        exitInstructionsEditor();
      }
      onOpenChange(nextOpen);
    },
    [exitInstructionsEditor, isSubmitting, onOpenChange]
  );

  const handlePauseToggle = () => {
    if (!routine || isSubmitting) return;
    if (routine.isPaused) {
      void resumeRoutine(routine.referenceId);
    } else {
      void routines.pauseRoutine(routine.referenceId).catch(() => undefined);
    }
  };
  const handleDelete = () => {
    if (!routine || isSubmitting) return;
    setIsConfirmingDelete(true);
    void showDeleteRoutineModal(routine).then((confirmed) => {
      if (confirmed) {
        handleOpenChange(false);
      } else {
        setIsConfirmingDelete(false);
      }
    });
  };

  const withModalOnTop = async <T,>(show: () => Promise<T>): Promise<T> => {
    setIsShowingModalOnTop(true);
    try {
      return await show();
    } finally {
      setIsShowingModalOnTop(false);
    }
  };

  const webhookEndpointActions = useWebhookEndpointActions({
    context: 'CreateRoutineModal',
    wrapModal: withModalOnTop,
  });

  /** The draft stays in the draft store, and reopening the form restores it. */
  const handleManageWebhookEndpoints = () => {
    if (isSubmitting) return;
    if (closeOnNavigate) handleOpenChange(false);
    void openSettings('Webhooks');
  };

  const snapshotCustomDraft = () => {
    routineDrafts.setCustomBackup(draftOwnerId, draft);
  };
  const handleDiscardDraft = () => {
    clearDraft();
    clearCreateFlowState();
    setDraftRestored(false);
  };

  const handleEditInstructions = useCallback(() => {
    setInstructionsDraft(prompt);
    isEditingInstructionsRef.current = true;
    setIsEditingInstructions(true);
  }, [prompt]);
  const handleSaveInstructions = useCallback(() => {
    if (!instructionsDraft.trim()) return;
    updateDraft({ prompt: instructionsDraft });
    exitInstructionsEditor();
  }, [exitInstructionsEditor, instructionsDraft, updateDraft]);
  const handleDiscardInstructions = exitInstructionsEditor;

  const handleSelectTemplate = (item: RoutineLibraryItem) => {
    // Re-selecting the same template shouldn't discard tweaks made in configure.
    if (item.id === selectedTemplateId) {
      updateCreateFlowState({ step: 'configure' });
      return;
    }
    if (selectedTemplateId === CUSTOM_TEMPLATE_ID) snapshotCustomDraft();
    routineDrafts.setDraft(
      draftOwnerId,
      draftKey,
      makeDraftFromLibraryItem(item)
    );
    updateCreateFlowState({
      step: 'configure',
      selectedTemplateId: item.id,
    });
  };

  const handleSelectCustom = () => {
    // Already on custom — preserve the in-progress draft verbatim.
    if (selectedTemplateId === CUSTOM_TEMPLATE_ID) {
      updateCreateFlowState({ step: 'configure' });
      return;
    }
    routineDrafts.setDraft(
      draftOwnerId,
      draftKey,
      routineDrafts.getCustomBackup(draftOwnerId) ?? makeDefaultDraft()
    );
    updateCreateFlowState({
      step: 'configure',
      selectedTemplateId: CUSTOM_TEMPLATE_ID,
    });
  };

  const trackRoutineUpdated = (updatedRoutine: Routine) => {
    trackEvent('REPORT_TEMPLATE_UPDATED', {
      id: updatedRoutine.referenceId,
      title: updatedRoutine.title,
      templateId: selectedTemplateId,
    });
    trackEvent('ROUTINE_EVENT', {
      actionName: 'report_template_updated',
      id: updatedRoutine.referenceId,
      title: updatedRoutine.title,
      templateId: selectedTemplateId,
    });
  };

  // A routine the user cannot edit accepts only the notification fields, and
  // the rest of its draft skips validation because the user cannot fix it
  const saveNotificationSettings = async (
    editedRoutine: Routine
  ): Promise<boolean> => {
    setIsSubmitting(true);
    try {
      await routines.updateRoutine(editedRoutine.referenceId, {
        notificationsEnabled: draft.notificationsEnabled,
        emailNotificationsEnabled: draft.emailNotificationsEnabled,
        mayReachOut: draft.mayReachOut,
      });
      trackRoutineUpdated(editedRoutine);
      clearDraft();
      clearCreateFlowState();
      handleOpenChange(false, true);
      return true;
    } catch (error) {
      const fallback = 'Failed to update routine';
      toast.error(
        error instanceof HttpError && error.userErrorMessage
          ? error.userErrorMessage
          : fallback
      );
      logError(error, fallback, { context: 'CreateRoutineModal' });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Resolves `true` once the routine is saved */
  const handleSubmit = async (): Promise<boolean> => {
    if (routine && notificationsOnly) {
      return saveNotificationSettings(routine);
    }

    if (!draft.scheduleEnabled && draft.triggers.length === 0) {
      setSubmitAttempted(true);
      document.getElementById('routine-trigger-editor')?.focus();
      return false;
    }

    if (intervalEndError) {
      setSubmitAttempted(true);
      document.getElementById('routine-trigger-editor')?.focus();
      return false;
    }

    const parsedDraft = RoutineFormSubmissionSchema.safeParse(draft);
    if (!parsedDraft.success) {
      setSubmitAttempted(true);
      const firstInvalidField = parsedDraft.error.issues[0]?.path[0];
      if (firstInvalidField === 'prompt') {
        handleEditInstructions();
      } else if (firstInvalidField === 'title') {
        document.getElementById('routine-title')?.focus();
      } else {
        document.getElementById('routine-trigger-editor')?.focus();
      }
      return false;
    }

    const submittedDraft = parsedDraft.data;
    const payload = buildRoutineUpsertInput(submittedDraft, {
      storedRecurrenceType: routine?.recurrenceType,
      storedRecurrenceRule: routine?.recurrenceRule ?? undefined,
    });

    setIsSubmitting(true);
    try {
      if (isEditMode && routine) {
        await routines.updateRoutine(routine.referenceId, payload);
        trackRoutineUpdated(routine);
      } else {
        const { routine: created, initialReport } =
          await routines.createRoutine(payload);
        if (initialReport) reports.addApiReport(initialReport);
        const source =
          selectedTemplateId !== CUSTOM_TEMPLATE_ID
            ? 'library_template'
            : 'custom';
        const createdProperties = {
          id: created.referenceId,
          title: created.title,
          templateId: selectedTemplateId,
          source,
          templateSource: templateSourceFor(selectedTemplateId, libraryItems),
          frequency: submittedDraft.recurrenceType,
          deliveryChannel: submittedDraft.deliveryChannel,
          titleEdited: submittedDraft.title !== initialDraft.title,
          promptEdited: submittedDraft.prompt !== initialDraft.prompt,
          scheduleEdited:
            submittedDraft.recurrenceType !== initialDraft.recurrenceType ||
            submittedDraft.triggerTime !== initialDraft.triggerTime ||
            submittedDraft.monthDay !== initialDraft.monthDay ||
            submittedDraft.weekDays.join() !== initialDraft.weekDays.join() ||
            submittedDraft.advancedMonths.join() !==
              initialDraft.advancedMonths.join() ||
            submittedDraft.advancedDayMode !== initialDraft.advancedDayMode ||
            submittedDraft.advancedMonthDays.join() !==
              initialDraft.advancedMonthDays.join() ||
            submittedDraft.advancedTimes.join() !==
              initialDraft.advancedTimes.join() ||
            submittedDraft.intervalCount !== initialDraft.intervalCount ||
            submittedDraft.intervalUnit !== initialDraft.intervalUnit ||
            submittedDraft.intervalEndAt !== initialDraft.intervalEndAt,
        };
        trackEvent('REPORT_TEMPLATE_CREATED', createdProperties);
        trackEvent('ROUTINE_EVENT', {
          actionName: 'report_template_created',
          ...createdProperties,
        });
        toast.info('Routine created', {
          action: {
            label: 'View Routine',
            onClick: () => {
              navigate({
                to: '/routines/$routineId',
                params: { routineId: String(created.referenceId) },
              });
            },
          },
          description: initialReport
            ? 'Your first note is being generated'
            : 'It runs when the trigger fires',
          closeButton: true,
          duration: 5000,
          position: 'bottom-right',
        });
        await subscription.refetch();
      }
      clearDraft();
      clearCreateFlowState();
      handleOpenChange(false, true);
      return true;
    } catch (error) {
      if (
        error instanceof Error &&
        error.message === FreeLimitExceededError.RoutineLimitExceeded
      ) {
        handleOpenChange(false, true);
      } else {
        const fallback = isEditMode
          ? 'Failed to update routine'
          : 'Failed to create routine';
        toast.error(
          error instanceof HttpError && error.userErrorMessage
            ? error.userErrorMessage
            : fallback
        );
        logError(error, fallback, { context: 'CreateRoutineModal' });
      }
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };
  const restartWaitingRun = useRestartWaitingRun({
    saveRoutine: handleSubmit,
    wrapModal: withModalOnTop,
  });

  const outgoingRule = useCallback(
    (source: RoutineFormDraft) =>
      source.preservedRecurrenceRule ??
      buildRoutineRecurrenceRule(toRoutineScheduleParts(source)),
    []
  );
  // Match the API: require an upcoming run only when the schedule changes.
  const scheduleEdited =
    !editBaseline || outgoingRule(draft) !== outgoingRule(editBaseline);
  const intervalEndError =
    scheduleEdited && hasNoUpcomingIntervalOccurrence(draft)
      ? ROUTINE_INTERVAL_END_ERROR
      : undefined;

  const draftEdited = useMemo(
    () => !routineFormDraftsEqual(draft, initialDraft),
    [draft, initialDraft]
  );
  const instructionsDirty = instructionsDraft !== prompt;

  const isDirty = !isEditMode || draftEdited;
  const notificationsEdited =
    notificationsOnly && onlyNotificationsChanged(draft, initialDraft);
  const canSubmit =
    !isSubmitting &&
    (notificationsOnly
      ? notificationsEdited
      : isDirty && (draft.scheduleEnabled || draft.triggers.length > 0));
  const submitDisabledReason =
    notificationsOnly && draftEdited && !notificationsEdited
      ? 'Only notifications can be changed here. Use Improve this routine to change what it does.'
      : undefined;
  const instructionsValid = instructionsDraft.trim().length > 0;

  const validationResult = RoutineFormSubmissionSchema.safeParse(draft);
  const fieldErrors = validationResult.success
    ? null
    : validationResult.error.flatten().fieldErrors;
  const titleError = submitAttempted ? fieldErrors?.title?.[0] : undefined;
  const promptError = submitAttempted ? fieldErrors?.prompt?.[0] : undefined;
  const scheduleFieldError =
    fieldErrors?.triggerTime?.[0] ??
    fieldErrors?.hourlyEndTime?.[0] ??
    fieldErrors?.advancedMonths?.[0] ??
    fieldErrors?.advancedMonthDays?.[0] ??
    fieldErrors?.advancedTimes?.[0] ??
    fieldErrors?.intervalCount?.[0] ??
    fieldErrors?.intervalEndAt?.[0] ??
    fieldErrors?.weekDays?.[0] ??
    fieldErrors?.monthDay?.[0] ??
    intervalEndError;
  const scheduleError = submitAttempted
    ? draft.scheduleEnabled
      ? (scheduleFieldError ?? fieldErrors?.triggers?.[0])
      : (fieldErrors?.triggers?.[0] ?? undefined)
    : undefined;
  const headerTitle = isEditingInstructions
    ? 'Instructions'
    : isEditMode
      ? 'Configure routine'
      : step === 'pick'
        ? 'Create a new routine'
        : 'Configure routine';
  const headerDescription =
    step === 'pick'
      ? 'Routines run on a schedule and deliver notes you can review later'
      : null;

  const configureStepContent = (
    <ConfigureStep
      isEditMode={isEditMode}
      routine={routine}
      actions={actions}
      draft={draft}
      onDraftChange={updateDraft}
      onEditInstructions={handleEditInstructions}
      onIconPickerOpenChange={setIsIconPickerOpen}
      onImproveRoutine={onImproveRoutine}
      improveRoutineDisabledReason={improveRoutineDisabledReason}
      waitingRunNote={
        routine &&
        !notificationsOnly && (
          <RestartWaitingRunNote
            routine={routine}
            disabled={isSubmitting}
            onRestart={(waitingDevices) =>
              void restartWaitingRun(routine.referenceId, waitingDevices)
            }
          />
        )
      }
      isSubmitting={isSubmitting}
      hasScheduleTrigger={draft.scheduleEnabled}
      advancedSchedulesEnabled={advancedSchedulesEnabled}
      onAddScheduleTrigger={() =>
        updateDraft({ scheduleEnabled: true, preservedRecurrenceRule: null })
      }
      onRemoveScheduleTrigger={() =>
        updateDraft({ scheduleEnabled: false, preservedRecurrenceRule: null })
      }
      triggersEnabled={triggersEnabled}
      gmailAccounts={gmailAccounts ?? []}
      outlookAccounts={outlookAccounts ?? []}
      onConfigureEmailTrigger={(trigger, index) => {
        if (isSubmitting) return;
        updateDraft({ triggers: markSentToChat(draft.triggers, index) });
        if (closeOnNavigate) handleOpenChange(false);
        startEmailTriggerChat({
          routine,
          draft,
          trigger,
          gmailAccounts: gmailAccounts ?? [],
          outlookAccounts: outlookAccounts ?? [],
        });
      }}
      titleError={titleError}
      promptError={promptError}
      scheduleError={scheduleError}
      pauseActionPending={isPauseActionPending}
      onPauseToggle={handlePauseToggle}
      onDelete={handleDelete}
      webhookEndpoints={webhookEndpoints}
      onCreateWebhookEndpoint={webhookEndpointActions.create}
      onManageWebhookEndpoints={handleManageWebhookEndpoints}
    />
  );
  const instructionsStepContent = (
    <InstructionsStep
      value={instructionsDraft}
      syncVersion={instructionsSyncVersion}
      findPortalContainer={findPortalContainer}
      onChange={setInstructionsDraft}
    />
  );

  return (
    <Dialog.Root
      open={open && !isConfirmingDelete && !isShowingModalOnTop}
      onOpenChange={handleOpenChange}
    >
      <Dialog.Content
        className="h-[1024px] max-h-[85vh] w-[95vw] max-w-[1024px] flex flex-col gap-0 p-0"
        onOpenAutoFocus={(event) => {
          if (!isEditMode && step !== 'configure') return;
          const titleInput = document.getElementById('routine-title');
          if (!(titleInput instanceof HTMLInputElement)) return;
          event.preventDefault();
          titleInput.focus();
        }}
        onEscapeKeyDown={(event) => {
          if (document.activeElement?.closest('[data-editor-floating-ui]')) {
            event.preventDefault();
            return;
          }
          if (!isEditingInstructions) return;
          event.preventDefault();
          handleDiscardInstructions();
        }}
      >
        {isEditingInstructions && (
          <div
            ref={setFindPortalContainer}
            data-routine-find-portal=""
            className="pointer-events-none absolute right-12 top-4 z-20"
          />
        )}
        <Dialog.Header className="px-6 pt-6 pb-4 shrink-0">
          <XStack className="min-w-0 items-center gap-2">
            {isEditingInstructions && (
              <button
                type="button"
                aria-label="Back to routine settings"
                onClick={handleDiscardInstructions}
                className="-ml-2 flex size-8 shrink-0 items-center justify-center rounded-md text-foreground transition-colors hover:bg-surface-hover focus-ring"
              >
                <Icons.arrowLeft className="size-4" />
              </button>
            )}
            <YStack className="gap-1 min-w-0">
              <Dialog.Title className="text-xl leading-tight">
                {headerTitle}
              </Dialog.Title>
              {headerDescription ? (
                <Dialog.Description className="text-sm text-foreground-muted">
                  {headerDescription}
                </Dialog.Description>
              ) : (
                <Dialog.Description className="sr-only">
                  {isEditingInstructions
                    ? 'Edit routine instructions'
                    : 'Configure routine details'}
                </Dialog.Description>
              )}
            </YStack>
          </XStack>
        </Dialog.Header>

        {isEditingInstructions ? (
          <div className="min-h-0 flex-1 pl-6">{instructionsStepContent}</div>
        ) : step === 'pick' && !isEditMode ? (
          <div className="min-h-0 flex-1 overflow-hidden pl-6">
            <PickStep onSelectTemplate={handleSelectTemplate} />
          </div>
        ) : (
          <ScrollMask
            showEndFade
            className={cn(
              'min-h-0 flex-1 overflow-x-hidden overflow-y-auto pb-6 pl-6 pr-3',
              isIconPickerOpen && 'overflow-y-hidden'
            )}
          >
            {configureStepContent}
          </ScrollMask>
        )}

        <Dialog.Footer
          className={cn(
            'py-4 pl-6 pr-[calc(0.75rem+8px)] shrink-0',
            '!flex-row gap-3 items-center',
            isEditingInstructions ? '!justify-between' : '!justify-end'
          )}
        >
          {isEditingInstructions && (
            <MicrophoneButton
              mediaStreamRef={mediaStreamRef}
              recordingStatus={recordingStatus}
              onStartRecording={startRecording}
              onStopRecording={stopRecording}
              onCancelRecording={cancelRecording}
              tooltipSide="top"
              enableHotkeys={false}
            />
          )}
          {draftRestored && step === 'configure' && !isEditingInstructions && (
            <XStack className="mr-auto items-center gap-1 text-sm text-foreground-muted">
              <Icons.history className="size-3.5" />
              <span>Draft restored</span>
              <Button
                variant="link"
                size="xs"
                onClick={handleDiscardDraft}
                disabled={isSubmitting}
              >
                Discard draft
              </Button>
            </XStack>
          )}
          <XStack className="gap-2 items-center min-h-9">
            {isEditingInstructions ? (
              <>
                <Button variant="secondary" onClick={handleDiscardInstructions}>
                  {instructionsDirty ? 'Discard' : 'Back'}
                </Button>
                <Button
                  onClick={handleSaveInstructions}
                  disabled={!instructionsDirty || !instructionsValid}
                >
                  Save
                </Button>
              </>
            ) : step === 'pick' && !isEditMode ? (
              <>
                <Button
                  variant="secondary"
                  onClick={() => handleOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button onClick={handleSelectCustom}>Start from scratch</Button>
              </>
            ) : (
              <>
                {isEditMode ? (
                  <Button
                    variant="secondary"
                    onClick={() => handleOpenChange(false)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    onClick={() => updateCreateFlowState({ step: 'pick' })}
                    disabled={isSubmitting}
                  >
                    {selectedTemplateId === CUSTOM_TEMPLATE_ID
                      ? 'Back'
                      : 'Change'}
                  </Button>
                )}
                <SubmitButton
                  disabled={!canSubmit}
                  disabledReason={submitDisabledReason}
                  onClick={handleSubmit}
                >
                  {isSubmitting
                    ? isEditMode
                      ? 'Saving...'
                      : 'Creating...'
                    : isEditMode
                      ? 'Save'
                      : 'Create'}
                </SubmitButton>
              </>
            )}
          </XStack>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
});

/**
 * A natively disabled button receives no pointer events, so a Save with a
 * `disabledReason` hangs the Tooltip on a focusable span around it instead.
 */
const SubmitButton = ({
  disabled,
  disabledReason,
  onClick,
  children,
}: {
  disabled: boolean;
  disabledReason?: string;
  onClick: () => void;
  children: ReactNode;
}) => {
  const button = (
    <Button
      id="submit-routine-button"
      onClick={onClick}
      disabled={disabled}
      className={cn(disabledReason && 'pointer-events-none')}
    >
      {children}
    </Button>
  );

  if (!disabledReason) return button;

  return (
    <Tooltip label={disabledReason}>
      <span tabIndex={0} className="inline-flex cursor-not-allowed">
        {button}
      </span>
    </Tooltip>
  );
};

type RoutineConfigureModalProps = {
  routine: Routine;
  closeOnNavigate?: boolean;
  open: boolean;
  onClose: () => void;
  onImproveRoutine?: () => void;
  improveRoutineDisabledReason?: string;
};

export function RoutineConfigureModal({
  routine,
  closeOnNavigate,
  open,
  onClose,
  onImproveRoutine,
  improveRoutineDisabledReason,
}: RoutineConfigureModalProps) {
  return (
    <CreateRoutineModal
      routine={routine}
      closeOnNavigate={closeOnNavigate}
      open={open}
      onImproveRoutine={onImproveRoutine}
      improveRoutineDisabledReason={improveRoutineDisabledReason}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    />
  );
}

type PickStepProps = {
  onSelectTemplate: (item: RoutineLibraryItem) => void;
};

function PickStep({ onSelectTemplate }: PickStepProps) {
  return (
    <LibraryBrowser
      onSelect={onSelectTemplate}
      gridClassName="grid grid-cols-1 gap-3"
      heading={null}
      scrollItems
    />
  );
}

function RoutineTriggerEditor({
  hasTrigger,
  advancedSchedulesEnabled,
  triggers,
  triggersEnabled,
  gmailAccounts,
  outlookAccounts,
  onConfigureEmailTrigger,
  emailConfigurationDisabled,
  emailEditingDisabled,
  recurrenceType,
  triggerTime,
  hourlyEndTime,
  advancedMonths,
  advancedDayMode,
  advancedMonthDays,
  advancedTimes,
  hasCustomSchedule,
  weekDays,
  monthDay,
  intervalCount,
  intervalUnit,
  intervalEndAt,
  onAdd,
  onRemove,
  onIntervalChange,
  onTriggersChange,
  onRecurrenceTypeChange,
  onTimeChange,
  onHourlyEndTimeChange,
  onAdvancedMonthsChange,
  onAdvancedDayModeChange,
  onAdvancedMonthDaysChange,
  onAdvancedTimesChange,
  onWeekDaysChange,
  onMonthDayChange,
  webhookEndpoints,
  onCreateWebhookEndpoint,
  onManageWebhookEndpoints,
  error,
}: {
  hasTrigger: boolean;
  advancedSchedulesEnabled: boolean;
  triggers: RoutineTriggerDraft[];
  triggersEnabled: RoutineTriggerKindsEnabled;
  gmailAccounts: GmailAccountOption[];
  outlookAccounts: GmailAccountOption[];
  onConfigureEmailTrigger: (trigger: EmailTriggerDraft, index: number) => void;
  emailConfigurationDisabled: boolean;
  emailEditingDisabled: boolean;
  webhookEndpoints: WebhookEndpoint[];
  onCreateWebhookEndpoint: () => Promise<string | null>;
  recurrenceType: RoutineFormDraft['recurrenceType'];
  triggerTime: string;
  hourlyEndTime: string;
  advancedMonths: number[];
  advancedDayMode: RoutineFormDraft['advancedDayMode'];
  advancedMonthDays: number[];
  advancedTimes: string[];
  hasCustomSchedule: boolean;
  weekDays: string[];
  monthDay: number;
  intervalCount: number;
  intervalUnit: RoutineFormDraft['intervalUnit'];
  intervalEndAt: string | null;
  onAdd: () => void;
  onRemove: () => void;
  onIntervalChange: (value: Partial<RoutineFormDraft>) => void;
  onTriggersChange: (value: RoutineTriggerDraft[]) => void;
  onRecurrenceTypeChange: (value: RoutineFormDraft['recurrenceType']) => void;
  onTimeChange: (value: string) => void;
  onHourlyEndTimeChange: (value: string) => void;
  onAdvancedMonthsChange: (value: number[]) => void;
  onAdvancedDayModeChange: (value: RoutineFormDraft['advancedDayMode']) => void;
  onAdvancedMonthDaysChange: (value: number[]) => void;
  onAdvancedTimesChange: (value: string[]) => void;
  onWeekDaysChange: (value: string[]) => void;
  onMonthDayChange: (value: number) => void;
  onManageWebhookEndpoints: () => void;
  error?: string;
}) {
  const enabledMeetingTriggerKinds = MEETING_TRIGGER_KINDS.filter(
    (kind) => triggersEnabled.meeting[kind]
  );
  const eventTriggersEnabled =
    triggersEnabled.gmail ||
    triggersEnabled.outlook ||
    triggersEnabled.webhook ||
    enabledMeetingTriggerKinds.length > 0;
  const selectedDayLabels = WEEK_DAYS.filter((day) =>
    weekDays.includes(day.code)
  ).map((day) => day.label);
  const weekDaysLabel =
    selectedDayLabels.length === 0
      ? 'Select days'
      : selectedDayLabels.length <= 2
        ? selectedDayLabels.join(', ')
        : `${selectedDayLabels.length} days`;
  const isWeekdays =
    weekDays.length === WEEKDAY_ONLY_CODES.length &&
    WEEKDAY_ONLY_CODES.every((day) => weekDays.includes(day));
  const isEveryDay =
    weekDays.length === WEEK_DAY_CODES.length &&
    WEEK_DAY_CODES.every((day) => weekDays.includes(day));
  const hourlyDaysLabel = isEveryDay
    ? 'Every day'
    : isWeekdays
      ? 'Weekdays'
      : `${weekDays.length} days`;
  const advancedMonthsLabel = EVERY_MONTH.filter((month) =>
    advancedMonths.includes(month)
  )
    .map((month) => MONTH_LABELS[month - 1])
    .join(', ');
  const advancedWeekDaysLabel = selectedDayLabels.join(', ');
  const advancedMonthDaysLabel = [...advancedMonthDays]
    .sort((a, b) => a - b)
    .join(', ');
  const sortedAdvancedTimes = [...advancedTimes].sort();
  const triggerFrequency: RoutineTriggerFrequency =
    recurrenceType === RecurrenceType.Weekly && isWeekdays
      ? WEEKDAYS_FREQUENCY
      : recurrenceType;
  const frequencyOptions = ROUTINE_FREQUENCY_OPTIONS.filter(
    (option) =>
      advancedSchedulesEnabled ||
      !option.isAdvancedOnly ||
      option.value === recurrenceType
  );

  const selectTriggerFrequency = (frequency: RoutineTriggerFrequency) => {
    if (frequency === triggerFrequency) return;
    if (frequency === WEEKDAYS_FREQUENCY) {
      onRecurrenceTypeChange(RecurrenceType.Weekly);
      onWeekDaysChange([...WEEKDAY_ONLY_CODES]);
      return;
    }
    onRecurrenceTypeChange(frequency);
    if (frequency === RecurrenceType.Advanced) {
      onAdvancedMonthsChange([...EVERY_MONTH]);
      onAdvancedTimesChange([triggerTime]);
      if (recurrenceType !== RecurrenceType.Weekly) {
        onWeekDaysChange([...WEEK_DAY_CODES]);
      }
      return;
    }
    if (frequency === RecurrenceType.Hourly) {
      onTimeChange('09:00');
      onHourlyEndTimeChange('18:00');
      onWeekDaysChange([...WEEK_DAY_CODES]);
      return;
    }
    if (frequency === RecurrenceType.Interval) {
      onIntervalChange({
        intervalCount: DEFAULT_ROUTINE_INTERVAL_COUNT,
        intervalUnit: DEFAULT_ROUTINE_INTERVAL_UNIT,
        intervalStartAt: null,
        intervalEndAt: null,
      });
      return;
    }
    if (frequency === RecurrenceType.Weekly && isWeekdays) {
      onWeekDaysChange(['MO']);
    }
  };

  // "every 30 minutes" must not become "every 30 days" when the unit changes
  const selectIntervalUnit = (unit: RoutineFormDraft['intervalUnit']) => {
    if (unit === intervalUnit) return;
    const counts = ROUTINE_INTERVAL_COUNTS[unit];
    onIntervalChange({
      intervalUnit: unit,
      intervalCount: counts.includes(intervalCount)
        ? intervalCount
        : DEFAULT_INTERVAL_COUNT_BY_UNIT[unit],
    });
  };

  const toggleWeekDay = (code: string) => {
    if (weekDays.includes(code)) {
      if (weekDays.length === 1) return;
      onWeekDaysChange(weekDays.filter((day) => day !== code));
      return;
    }
    onWeekDaysChange([...weekDays, code]);
  };
  const toggleAdvancedMonth = (month: number) => {
    if (advancedMonths.includes(month)) {
      if (advancedMonths.length === 1) return;
      onAdvancedMonthsChange(
        advancedMonths.filter((selectedMonth) => selectedMonth !== month)
      );
      return;
    }
    onAdvancedMonthsChange([...advancedMonths, month].sort((a, b) => a - b));
  };
  const selectAdvancedDayMode = (
    value: RoutineFormDraft['advancedDayMode']
  ) => {
    onAdvancedDayModeChange(value);
    if (value === 'weekDays' && weekDays.length === 0) {
      onWeekDaysChange([...WEEK_DAY_CODES]);
    }
    if (value === 'monthDays' && advancedMonthDays.length === 0) {
      onAdvancedMonthDaysChange([1]);
    }
  };
  const toggleAdvancedMonthDay = (day: number) => {
    if (advancedMonthDays.includes(day)) {
      if (advancedMonthDays.length === 1) return;
      onAdvancedMonthDaysChange(
        advancedMonthDays.filter((selectedDay) => selectedDay !== day)
      );
      return;
    }
    onAdvancedMonthDaysChange(
      [...advancedMonthDays, day].sort((a, b) => a - b)
    );
  };
  const updateAdvancedTime = (index: number, value: string) => {
    const nextTimes = syncAdvancedTime(advancedTimes, index, value);
    if (nextTimes !== advancedTimes) onAdvancedTimesChange(nextTimes);
  };
  const addAdvancedTime = () => {
    const minute = sortedAdvancedTimes[0]?.split(':')[1] ?? '00';
    const lastHour = Number(sortedAdvancedTimes.at(-1)?.split(':')[0] ?? -1);
    for (let offset = 1; offset <= 24; offset += 1) {
      const hour = (lastHour + offset) % 24;
      const nextTime = `${String(hour).padStart(2, '0')}:${minute}`;
      if (!advancedTimes.includes(nextTime)) {
        onAdvancedTimesChange([...advancedTimes, nextTime].sort());
        return;
      }
    }
  };

  return (
    <YStack
      id="routine-trigger-editor"
      role="group"
      tabIndex={-1}
      aria-label="Schedule trigger"
      aria-describedby={error ? 'routine-trigger-error' : undefined}
      aria-invalid={Boolean(error)}
      className="gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      <YStack className="gap-0.5">
        <Label className="font-medium text-sm">Trigger</Label>
        <p className="text-xs text-foreground-muted">
          Sets when this routine runs
        </p>
      </YStack>
      {hasTrigger && (
        <XStack
          role="group"
          aria-label="Time trigger"
          className="relative min-h-14 flex-wrap items-center gap-x-2 gap-y-[2px] rounded-xl border border-border bg-surface px-2 py-2"
        >
          <XStack className="h-[calc(3.5rem-1rem-2px)] shrink-0 items-center gap-2">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-foreground-muted">
              <Icons.calendarClock className="size-4 text-background" />
            </span>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <Button
                  aria-label={`Trigger frequency: ${triggerFrequency}`}
                  variant="outline"
                  size="sm"
                  className="gap-1.5 px-2"
                >
                  {triggerFrequency}
                  <Icons.chevronDown className="size-3.5" />
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="start" className="w-36">
                <DropdownMenu.RadioGroup
                  value={triggerFrequency}
                  onValueChange={(value) =>
                    selectTriggerFrequency(value as RoutineTriggerFrequency)
                  }
                >
                  {frequencyOptions.map((option) => (
                    <DropdownMenu.RadioItem
                      key={option.value}
                      value={option.value}
                    >
                      {option.value}
                    </DropdownMenu.RadioItem>
                  ))}
                </DropdownMenu.RadioGroup>
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          </XStack>

          {(recurrenceType === RecurrenceType.Advanced ||
            recurrenceType === RecurrenceType.Hourly ||
            recurrenceType === RecurrenceType.Interval) &&
            hasCustomSchedule && (
              <span className="text-sm text-foreground-muted">
                Custom schedule
              </span>
            )}

          {recurrenceType === RecurrenceType.Advanced && !hasCustomSchedule && (
            <div className="grid w-full basis-full grid-cols-[4.5rem_minmax(0,1fr)] items-start gap-x-2 gap-y-2 pl-10 pr-10 pt-1">
              <span className="pt-1.5 text-sm text-foreground-muted">
                Months
              </span>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <Button
                    aria-label={`Months: ${advancedMonthsLabel}`}
                    variant="outline"
                    size="sm"
                    className="w-full min-w-0 max-w-full gap-1.5 px-2 [&>span]:w-full [&>span]:min-w-0 [&>span]:justify-between"
                  >
                    <span className="min-w-0 flex-1 truncate text-left">
                      {advancedMonthsLabel}
                    </span>
                    <Icons.chevronDown className="size-3.5 shrink-0" />
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="start" className="w-40">
                  {EVERY_MONTH.map((month) => (
                    <DropdownMenu.CheckboxItem
                      key={month}
                      checked={advancedMonths.includes(month)}
                      disabled={
                        advancedMonths.length === 1 &&
                        advancedMonths.includes(month)
                      }
                      onSelect={(event) => event.preventDefault()}
                      onCheckedChange={() => toggleAdvancedMonth(month)}
                    >
                      {MONTH_LABELS[month - 1]}
                    </DropdownMenu.CheckboxItem>
                  ))}
                </DropdownMenu.Content>
              </DropdownMenu.Root>

              <span className="pt-1.5 text-sm text-foreground-muted">Days</span>
              <XStack className="min-w-0 flex-wrap items-center gap-2">
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger asChild>
                    <Button
                      aria-label="Day selection type"
                      variant="outline"
                      size="sm"
                      className="gap-1.5 px-2"
                    >
                      {advancedDayMode === 'weekDays'
                        ? 'Days of the week'
                        : 'Days of the month'}
                      <Icons.chevronDown className="size-3.5" />
                    </Button>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content align="start">
                    <DropdownMenu.RadioGroup
                      value={advancedDayMode}
                      onValueChange={(value) =>
                        selectAdvancedDayMode(
                          value as RoutineFormDraft['advancedDayMode']
                        )
                      }
                    >
                      <DropdownMenu.RadioItem value="weekDays">
                        Days of the week
                      </DropdownMenu.RadioItem>
                      <DropdownMenu.RadioItem value="monthDays">
                        Days of the month
                      </DropdownMenu.RadioItem>
                    </DropdownMenu.RadioGroup>
                  </DropdownMenu.Content>
                </DropdownMenu.Root>
                {advancedDayMode === 'weekDays' ? (
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger asChild>
                      <Button
                        aria-label={`Days: ${advancedWeekDaysLabel}`}
                        variant="outline"
                        size="sm"
                        className="min-w-0 max-w-full gap-1.5 px-2 [&>span]:min-w-0"
                      >
                        <span className="min-w-0 flex-1 truncate text-left">
                          {advancedWeekDaysLabel}
                        </span>
                        <Icons.chevronDown className="size-3.5 shrink-0" />
                      </Button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content align="start" className="w-32">
                      {WEEK_DAYS.map((day) => (
                        <DropdownMenu.CheckboxItem
                          key={day.code}
                          checked={weekDays.includes(day.code)}
                          disabled={
                            weekDays.length === 1 && weekDays.includes(day.code)
                          }
                          onSelect={(event) => event.preventDefault()}
                          onCheckedChange={() => toggleWeekDay(day.code)}
                        >
                          {day.label}
                        </DropdownMenu.CheckboxItem>
                      ))}
                    </DropdownMenu.Content>
                  </DropdownMenu.Root>
                ) : (
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger asChild>
                      <Button
                        aria-label={`Days: ${advancedMonthDaysLabel}`}
                        variant="outline"
                        size="sm"
                        className="min-w-0 max-w-full gap-1.5 px-2 [&>span]:min-w-0"
                      >
                        <span className="min-w-0 flex-1 truncate text-left">
                          {advancedMonthDaysLabel}
                        </span>
                        <Icons.chevronDown className="size-3.5 shrink-0" />
                      </Button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content
                      align="start"
                      className="max-h-72 w-32 overflow-y-auto"
                    >
                      {MONTH_DAYS.map((day) => (
                        <DropdownMenu.CheckboxItem
                          key={day}
                          checked={advancedMonthDays.includes(day)}
                          disabled={
                            advancedMonthDays.length === 1 &&
                            advancedMonthDays.includes(day)
                          }
                          onSelect={(event) => event.preventDefault()}
                          onCheckedChange={() => toggleAdvancedMonthDay(day)}
                        >
                          Day {day}
                        </DropdownMenu.CheckboxItem>
                      ))}
                    </DropdownMenu.Content>
                  </DropdownMenu.Root>
                )}
              </XStack>

              <span className="pt-1.5 text-sm text-foreground-muted">Time</span>
              <XStack className="min-w-0 flex-wrap items-center gap-2">
                {sortedAdvancedTimes.map((time, displayIndex) => {
                  const index = advancedTimes.indexOf(time);
                  return (
                    <XStack key={index} className="items-center gap-0.5">
                      <TimePickerPopover
                        selectedTime={time}
                        onTimeSelected={(value) =>
                          updateAdvancedTime(index, value)
                        }
                        triggerClassName="h-8 w-auto gap-1.5 px-2"
                        triggerIcon="chevronDown"
                        triggerAriaLabel={`Time ${displayIndex + 1}`}
                      />
                      <Button
                        aria-label={`Remove time ${displayIndex + 1}`}
                        variant="ghost"
                        size="icon-sm"
                        iconOnly
                        className="text-foreground-muted"
                        disabled={advancedTimes.length === 1}
                        onClick={() =>
                          onAdvancedTimesChange(
                            advancedTimes.filter(
                              (_, timeIndex) => timeIndex !== index
                            )
                          )
                        }
                      >
                        <Icons.cancel />
                      </Button>
                    </XStack>
                  );
                })}
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1 px-2"
                  disabled={advancedTimes.length >= 24}
                  onClick={addAdvancedTime}
                >
                  <Icons.plus className="size-3.5" />
                  Add time
                </Button>
              </XStack>
            </div>
          )}

          {recurrenceType === RecurrenceType.Hourly && !hasCustomSchedule && (
            <>
              <span className="text-sm text-foreground-muted">on</span>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <Button
                    aria-label={`Hourly days: ${hourlyDaysLabel}`}
                    variant="outline"
                    size="sm"
                    className="gap-1.5 px-2"
                  >
                    {hourlyDaysLabel}
                    <Icons.chevronDown className="size-3.5" />
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="start" className="w-32">
                  <DropdownMenu.RadioGroup
                    value={
                      isEveryDay ? 'every-day' : isWeekdays ? 'weekdays' : ''
                    }
                    onValueChange={(value) =>
                      onWeekDaysChange(
                        value === 'every-day'
                          ? [...WEEK_DAY_CODES]
                          : [...WEEKDAY_ONLY_CODES]
                      )
                    }
                  >
                    <DropdownMenu.RadioItem value="every-day">
                      Every day
                    </DropdownMenu.RadioItem>
                    <DropdownMenu.RadioItem value="weekdays">
                      Weekdays
                    </DropdownMenu.RadioItem>
                  </DropdownMenu.RadioGroup>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
              <XStack className="items-center gap-2">
                <span className="text-sm text-foreground-muted">from</span>
                <TimePickerPopover
                  selectedTime={triggerTime}
                  onTimeSelected={(value) => {
                    onTimeChange(value);
                    onHourlyEndTimeChange(
                      `${hourlyEndTime.split(':')[0]}:${value.split(':')[1]}`
                    );
                  }}
                  triggerClassName="h-8 w-auto gap-1.5 px-2"
                  triggerIcon="chevronDown"
                  triggerAriaLabel="Hourly start time"
                />
                <span className="text-sm text-foreground-muted">to</span>
                <TimePickerPopover
                  selectedTime={hourlyEndTime}
                  onTimeSelected={(value) => {
                    onTimeChange(
                      `${triggerTime.split(':')[0]}:${value.split(':')[1]}`
                    );
                    onHourlyEndTimeChange(value);
                  }}
                  triggerClassName="h-8 w-auto gap-1.5 px-2"
                  triggerIcon="chevronDown"
                  triggerAriaLabel="Hourly end time"
                />
              </XStack>
            </>
          )}

          {recurrenceType === RecurrenceType.Interval && !hasCustomSchedule && (
            <>
              <span className="text-sm text-foreground-muted">every</span>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <Button
                    aria-label={`Interval: every ${intervalCount} ${INTERVAL_UNIT_LABELS[intervalUnit]}`}
                    variant="outline"
                    size="sm"
                    className="shrink-0 gap-1.5 px-2"
                  >
                    {intervalCount}
                    <Icons.chevronDown className="size-3.5" />
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content
                  align="start"
                  className="max-h-52 w-24 overflow-y-auto"
                >
                  <DropdownMenu.RadioGroup
                    value={String(intervalCount)}
                    onValueChange={(value) =>
                      onIntervalChange({ intervalCount: Number(value) })
                    }
                  >
                    {routineIntervalCounts(intervalUnit, intervalCount).map(
                      (count) => (
                        <DropdownMenu.RadioItem
                          key={count}
                          value={String(count)}
                        >
                          {count}
                        </DropdownMenu.RadioItem>
                      )
                    )}
                  </DropdownMenu.RadioGroup>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <Button
                    aria-label={`Interval unit: ${INTERVAL_UNIT_LABELS[intervalUnit]}`}
                    variant="outline"
                    size="sm"
                    className="shrink-0 gap-1.5 px-2"
                  >
                    {INTERVAL_UNIT_LABELS[intervalUnit]}
                    <Icons.chevronDown className="size-3.5" />
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="start" className="w-32">
                  <DropdownMenu.RadioGroup
                    value={intervalUnit}
                    onValueChange={(value) =>
                      selectIntervalUnit(
                        value as RoutineFormDraft['intervalUnit']
                      )
                    }
                  >
                    {ROUTINE_INTERVAL_UNITS.map((unit) => (
                      <DropdownMenu.RadioItem key={unit} value={unit}>
                        {INTERVAL_UNIT_LABELS[unit]}
                      </DropdownMenu.RadioItem>
                    ))}
                  </DropdownMenu.RadioGroup>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
              <span className="text-sm text-foreground-muted">ends</span>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <Button
                    aria-label={`Interval end: ${intervalEndAt ? 'on date' : 'never'}`}
                    variant="outline"
                    size="sm"
                    className="shrink-0 gap-1.5 px-2"
                  >
                    {intervalEndAt ? 'on date' : 'never'}
                    <Icons.chevronDown className="size-3.5" />
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="start" className="w-28">
                  <DropdownMenu.RadioGroup
                    value={intervalEndAt ? 'date' : 'never'}
                    onValueChange={(value) =>
                      onIntervalChange({
                        intervalEndAt:
                          value === 'never' ? null : defaultIntervalEnd(),
                      })
                    }
                  >
                    <DropdownMenu.RadioItem value="never">
                      Never
                    </DropdownMenu.RadioItem>
                    <DropdownMenu.RadioItem value="date">
                      On date
                    </DropdownMenu.RadioItem>
                  </DropdownMenu.RadioGroup>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
              {intervalEndAt && (
                <XStack className="min-w-0 flex-wrap items-center gap-x-2 gap-y-[2px]">
                  <Input
                    type="date"
                    aria-label="Interval end date"
                    className="h-8 w-[8.5rem] shrink-0"
                    value={intervalEndAt.split('T')[0]}
                    onChange={(event) =>
                      onIntervalChange({
                        intervalEndAt: event.target.value
                          ? `${event.target.value}T${intervalEndAt.split('T')[1] ?? '09:00'}`
                          : null,
                      })
                    }
                  />
                  <TimePickerPopover
                    selectedTime={intervalEndAt.split('T')[1] ?? '09:00'}
                    onTimeSelected={(value) =>
                      onIntervalChange({
                        intervalEndAt: `${intervalEndAt.split('T')[0]}T${value}`,
                      })
                    }
                    triggerClassName="h-8 w-auto shrink-0 gap-1.5 px-2"
                    triggerIcon="chevronDown"
                    triggerAriaLabel="Interval end time"
                  />
                </XStack>
              )}
            </>
          )}

          {recurrenceType === RecurrenceType.Weekly && (
            <>
              <span className="text-sm text-foreground-muted">on</span>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <Button
                    aria-label={`Trigger days: ${weekDaysLabel}`}
                    variant="outline"
                    size="sm"
                    className="max-w-28 gap-1.5 px-2"
                  >
                    <span className="truncate">{weekDaysLabel}</span>
                    <Icons.chevronDown className="size-3.5 shrink-0" />
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="start" className="w-36">
                  {WEEK_DAYS.map((day) => (
                    <DropdownMenu.CheckboxItem
                      key={day.code}
                      checked={weekDays.includes(day.code)}
                      onSelect={(event) => event.preventDefault()}
                      onCheckedChange={() => toggleWeekDay(day.code)}
                    >
                      {day.label}
                    </DropdownMenu.CheckboxItem>
                  ))}
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </>
          )}

          {recurrenceType === RecurrenceType.Monthly && (
            <>
              <span className="text-xs text-foreground-muted">on</span>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <Button
                    aria-label={`Day of month: ${monthDay}`}
                    variant="outline"
                    size="sm"
                    className="gap-1.5 px-2"
                  >
                    Day {monthDay}
                    <Icons.chevronDown className="size-3.5" />
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content
                  align="start"
                  className="max-h-52 w-28 overflow-y-auto"
                >
                  {MONTH_DAYS.map((day) => (
                    <DropdownMenu.Item
                      key={day}
                      onSelect={() => onMonthDayChange(day)}
                    >
                      Day {day}
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </>
          )}

          {recurrenceType !== RecurrenceType.Advanced &&
            recurrenceType !== RecurrenceType.Hourly &&
            recurrenceType !== RecurrenceType.Interval && (
              <>
                <span className="text-sm text-foreground-muted">at</span>
                <TimePickerPopover
                  selectedTime={triggerTime}
                  onTimeSelected={onTimeChange}
                  triggerClassName="h-8 w-auto gap-1.5 px-2"
                  triggerIcon="chevronDown"
                />
              </>
            )}
          <Button
            aria-label="Remove trigger"
            variant="ghost"
            size="icon-sm"
            iconOnly
            className="absolute right-3 top-[13px] shrink-0 text-foreground-muted"
            onClick={onRemove}
          >
            <Icons.trash2 />
          </Button>
        </XStack>
      )}

      {triggers.map((trigger, index) => {
        const key = `${trigger.kind}-${index}`;
        // Editing a row handed to chat takes it back from the agent
        const onChange = (next: RoutineTriggerDraft) =>
          onTriggersChange(
            triggers.map((existing, i) =>
              i === index ? { ...next, sentToChat: undefined } : existing
            )
          );
        const onRemove = () =>
          onTriggersChange(triggers.filter((_, i) => i !== index));
        switch (trigger.kind) {
          case ROUTINE_TRIGGER_GMAIL:
            return (
              <RoutineGmailTriggerRow
                key={key}
                trigger={trigger}
                gmailAccounts={gmailAccounts}
                onAccountChange={(connectedAccountId) =>
                  onChange({ ...trigger, connectedAccountId })
                }
                onDirectionChange={(labelIds) =>
                  onChange({ ...trigger, labelIds })
                }
                onConfigure={() => onConfigureEmailTrigger(trigger, index)}
                configurationDisabled={emailConfigurationDisabled}
                editingDisabled={emailEditingDisabled}
                onRemove={onRemove}
              />
            );
          case ROUTINE_TRIGGER_OUTLOOK:
            return (
              <RoutineOutlookTriggerRow
                key={key}
                trigger={trigger}
                outlookAccounts={outlookAccounts}
                onConfigure={() => onConfigureEmailTrigger(trigger, index)}
                configurationDisabled={emailConfigurationDisabled}
                editingDisabled={emailEditingDisabled}
                onChange={onChange}
                onRemove={onRemove}
              />
            );
          case ROUTINE_TRIGGER_WEBHOOK:
            return (
              <WebhookTriggerRow
                key={key}
                trigger={trigger}
                endpoints={webhookEndpoints}
                onChange={onChange}
                onRemove={onRemove}
                onCreateEndpoint={onCreateWebhookEndpoint}
                onManageEndpoints={onManageWebhookEndpoints}
              />
            );
          case ROUTINE_TRIGGER_MEETING_STARTED:
          case ROUTINE_TRIGGER_MEETING_SUMMARIZED:
            return (
              <RoutineMeetingTriggerRow
                key={key}
                trigger={trigger}
                onChange={onChange}
                onRemove={onRemove}
              />
            );
          default:
            assertNever(trigger);
            return null;
        }
      })}

      {(!hasTrigger || eventTriggersEnabled) && (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <Button
              variant="outline"
              className="h-14 w-full border-dashed px-3 shadow-none hover:enabled:bg-surface-hover [&>span]:w-full [&>span]:justify-start"
            >
              <Icons.plus className="size-3.5" />
              Add trigger
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content
            align="start"
            className="w-[var(--radix-dropdown-menu-trigger-width)]"
          >
            {!hasTrigger && (
              <DropdownMenu.Label className="pb-1 pt-2">
                Schedule
              </DropdownMenu.Label>
            )}
            {!hasTrigger &&
              frequencyOptions.map((option) => (
                <DropdownMenu.Item
                  key={option.value}
                  className="items-start"
                  onSelect={() => {
                    selectTriggerFrequency(option.value);
                    onAdd();
                  }}
                >
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-foreground-muted">
                    <Icons.calendarClock className="size-4 text-background" />
                  </span>
                  <YStack className="min-w-0 gap-0.5">
                    <span>{option.value}</span>
                    <span className="text-xs text-foreground-muted">
                      {option.description}
                    </span>
                  </YStack>
                </DropdownMenu.Item>
              ))}
            {eventTriggersEnabled && (
              <DropdownMenu.Label className="pb-1 pt-2">
                Event
              </DropdownMenu.Label>
            )}
            {triggersEnabled.gmail && (
              <DropdownMenu.Item
                className="items-start"
                onSelect={() =>
                  onTriggersChange([
                    ...triggers,
                    {
                      kind: ROUTINE_TRIGGER_GMAIL,
                      query: '',
                      labelIds: [...DEFAULT_GMAIL_TRIGGER_LABELS],
                      connectedAccountId:
                        gmailAccounts.length === 1 ? gmailAccounts[0].id : null,
                    },
                  ])
                }
              >
                <XStack
                  center
                  className="size-8 shrink-0 rounded-full border border-border bg-surface"
                >
                  <Icons.gmail className="size-4" />
                </XStack>
                <YStack className="min-w-0 gap-0.5">
                  <span>Gmail</span>
                  <span className="text-xs text-foreground-muted">
                    Runs when you receive or send mail, optionally matching a
                    filter
                  </span>
                </YStack>
              </DropdownMenu.Item>
            )}
            {triggersEnabled.outlook && (
              <DropdownMenu.Item
                className="items-start"
                onSelect={() =>
                  onTriggersChange([
                    ...triggers,
                    createOutlookTriggerDraft(
                      outlookAccounts.length === 1
                        ? outlookAccounts[0].id
                        : null
                    ),
                  ])
                }
              >
                <XStack
                  center
                  className="size-8 shrink-0 rounded-full border border-border bg-surface"
                >
                  <Icons.outlook className="size-4" />
                </XStack>
                <YStack className="min-w-0 gap-0.5">
                  <span>Outlook</span>
                  <span className="text-xs text-foreground-muted">
                    Runs when you receive or send mail, optionally matching a
                    filter
                  </span>
                </YStack>
              </DropdownMenu.Item>
            )}
            {triggersEnabled.webhook && (
              <DropdownMenu.Item
                className="items-start"
                onSelect={() =>
                  onTriggersChange([
                    ...triggers,
                    {
                      kind: ROUTINE_TRIGGER_WEBHOOK,
                      conditions: [],
                      webhookEndpointId:
                        webhookEndpoints.length === 1
                          ? webhookEndpoints[0].id
                          : null,
                    },
                  ])
                }
              >
                <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-foreground-muted">
                  <Icons.plugZap className="size-4 text-background" />
                </span>
                <YStack className="min-w-0 gap-0.5">
                  <span>Webhook</span>
                  <span className="text-xs text-foreground-muted">
                    Runs when an external system posts an event
                  </span>
                </YStack>
              </DropdownMenu.Item>
            )}
            {enabledMeetingTriggerKinds.map((kind) => {
              const copy = MEETING_TRIGGER_COPY[kind];
              return (
                <DropdownMenu.Item
                  key={kind}
                  className="items-start"
                  onSelect={() =>
                    onTriggersChange([
                      ...triggers,
                      createMeetingTriggerDraft(kind),
                    ])
                  }
                >
                  <XStack
                    center
                    className="size-8 shrink-0 rounded-full bg-foreground-muted"
                  >
                    <copy.icon className="size-4 text-background" />
                  </XStack>
                  <YStack className="min-w-0 gap-0.5">
                    <span>{copy.title}</span>
                    <span className="text-xs text-foreground-muted">
                      {copy.description}
                    </span>
                  </YStack>
                </DropdownMenu.Item>
              );
            })}
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      )}
      <p
        id="routine-trigger-error"
        aria-live="polite"
        className="text-xs text-danger-11"
      >
        {error ?? ''}
      </p>
    </YStack>
  );
}

function InstructionsStep({
  value,
  syncVersion,
  findPortalContainer,
  onChange,
}: {
  value: string;
  syncVersion: number;
  findPortalContainer: HTMLElement | null;
  onChange: (value: string) => void;
}) {
  return (
    <ScrollMask
      showEndFade
      className="-ml-3 h-full overflow-x-hidden overflow-y-auto"
    >
      <div className="h-full [&>div]:h-full">
        <MarkdownEditor
          id="routine-instructions-editor"
          autofocus
          initialContent={value}
          syncedContent={value}
          syncedContentVersion={syncVersion}
          findPortalContainer={findPortalContainer}
          placeholder="Write detailed instructions for this routine"
          placeholderOnlyWhenEmpty
          ariaLabel="Routine instructions"
          featureSet="email"
          className="h-full !p-3 [&_.ProseMirror]:min-h-full"
          onUpdate={onChange}
        />
      </div>
    </ScrollMask>
  );
}

type ConfigureStepProps = {
  isEditMode: boolean;
  routine?: Routine;
  /** Set whenever `routine` is */
  actions?: RoutineActionState;
  draft: RoutineFormDraft;
  onDraftChange: (updates: Partial<RoutineFormDraft>) => void;
  onEditInstructions: () => void;
  onIconPickerOpenChange: (open: boolean) => void;
  onImproveRoutine?: () => void;
  improveRoutineDisabledReason?: string;
  waitingRunNote?: ReactNode;
  /** True while a create or update is in flight; gates lifecycle actions */
  isSubmitting: boolean;
  hasScheduleTrigger: boolean;
  advancedSchedulesEnabled: boolean;
  onAddScheduleTrigger: () => void;
  onRemoveScheduleTrigger: () => void;
  triggersEnabled: RoutineTriggerKindsEnabled;
  gmailAccounts: GmailAccountOption[];
  outlookAccounts: GmailAccountOption[];
  onConfigureEmailTrigger: (trigger: EmailTriggerDraft, index: number) => void;
  titleError?: string;
  promptError?: string;
  scheduleError?: string;
  pauseActionPending: boolean;
  onPauseToggle: () => void;
  onDelete: () => void;
  webhookEndpoints: WebhookEndpoint[];
  onCreateWebhookEndpoint: () => Promise<string | null>;
  onManageWebhookEndpoints: () => void;
};

const ConfigureStep = observer(function ConfigureStep({
  isEditMode,
  routine,
  actions,
  draft,
  onDraftChange,
  onEditInstructions,
  onIconPickerOpenChange,
  onImproveRoutine,
  improveRoutineDisabledReason,
  waitingRunNote,
  isSubmitting,
  hasScheduleTrigger,
  advancedSchedulesEnabled,
  onAddScheduleTrigger,
  onRemoveScheduleTrigger,
  triggersEnabled,
  gmailAccounts,
  outlookAccounts,
  onConfigureEmailTrigger,
  titleError,
  promptError,
  scheduleError,
  pauseActionPending,
  onPauseToggle,
  onDelete,
  webhookEndpoints,
  onCreateWebhookEndpoint,
  onManageWebhookEndpoints,
}: ConfigureStepProps) {
  const {
    title,
    icon,
    iconSuggestionsEnabled,
    prompt,
    recurrenceType,
    triggerTime,
    hourlyEndTime,
    advancedMonths,
    advancedDayMode,
    advancedMonthDays,
    advancedTimes,
    intervalCount,
    intervalUnit,
    intervalEndAt,
    preservedRecurrenceRule,
    weekDays,
    monthDay,
    triggers,
    notificationsEnabled,
    emailNotificationsEnabled,
    deliveryChannel,
    autoPauseEnabled,
    mayReachOut,
    agentMode,
  } = draft;
  const channelDeliveryEnabled = deliveryChannel !== MESSAGING_DELIVERY_OFF;
  const hasCustomSchedule = preservedRecurrenceRule !== null;
  const canTogglePause = routine?.isPaused
    ? actions?.canResume
    : actions?.canPause;
  const agentModeLocked = Boolean(
    actions && !actions.permissions.canChangeAgentMode
  );
  const deliveryLocked = Boolean(
    actions && !actions.permissions.canChangeDeliverySettings
  );
  const reachOutAvailable = useFeature()(FEATURES.ROUTINE_REACH_OUT);
  const subscription = useSubscription();
  const maxEnabled = subscription.status.routines_limits?.max_enabled ?? false;
  const ultraEnabled =
    subscription.status.routines_limits?.ultra_enabled ?? false;
  const godEnabled = subscription.status.routines_limits?.god_enabled ?? false;
  const teamAllowedModes = subscriptionStore.teamAllowedChatModes;
  const currentIcon = icon ?? DEFAULT_ROUTINE_ICON;
  const instructionsPreview = useMemo(
    () =>
      getInstructionsPreviewMarkdown(
        prompt ||
          'Create a well rounded report on my ongoing projects, todos, and goals'
      ),
    [prompt]
  );
  const showIconSuggestionDismiss =
    iconSuggestionsEnabled && currentIcon.code !== DEFAULT_ROUTINE_ICON.code;

  const dismissIconSuggestion = () =>
    onDraftChange({
      icon: { ...DEFAULT_ROUTINE_ICON, color: currentIcon.color },
      iconSuggestionsEnabled: false,
    });
  const instructionsField = (
    <YStack className="min-w-0 gap-2">
      <Label className="font-medium text-sm">Instructions</Label>
      <button
        id="routine-prompt"
        type="button"
        aria-label="Edit instructions"
        aria-describedby={promptError ? 'routine-prompt-error' : undefined}
        className="min-h-10 w-full cursor-text rounded-lg border border-border-strong bg-surface px-3 py-2 text-left text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        onClick={onEditInstructions}
      >
        <div
          inert
          className={cn(
            'pointer-events-none line-clamp-5 [&_.markdown]:contents [&_.smoothdown]:contents',
            !prompt && 'opacity-60'
          )}
        >
          <Markdown
            id={`routine-instructions-preview-${routine?.id ?? 'draft'}`}
            content={instructionsPreview}
            className="prose-sm max-w-none [&>*]:!my-0"
          />
        </div>
      </button>
      {promptError && (
        <p
          id="routine-prompt-error"
          aria-live="polite"
          className="text-danger-11 text-xs"
        >
          {promptError}
        </p>
      )}
      {isEditMode && onImproveRoutine && (
        <button
          type="button"
          aria-label="Improve this routine"
          aria-describedby={
            improveRoutineDisabledReason ? 'improve-routine-status' : undefined
          }
          className="group flex w-full items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2.5 text-left transition-[background-color,border-color,transform] duration-150 hover:border-border-strong hover:bg-surface-hover active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-border disabled:hover:bg-surface disabled:active:scale-100"
          disabled={isSubmitting || !!improveRoutineDisabledReason}
          onClick={onImproveRoutine}
        >
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-surface-hover text-foreground transition-colors group-hover:bg-surface-active">
            <Icons.sparkle className="size-4" />
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-medium">
              Improve this routine
            </span>
            <span
              id={
                improveRoutineDisabledReason
                  ? 'improve-routine-status'
                  : undefined
              }
              className="block text-xs text-foreground-muted"
            >
              {improveRoutineDisabledReason ??
                'Chat with Littlebird to refine these instructions'}
            </span>
          </span>
        </button>
      )}
    </YStack>
  );

  return (
    <YStack className="gap-5">
      <div className="flex flex-col gap-5">
        <YStack className="w-full gap-5">
          {waitingRunNote}
          <YStack className="gap-2">
            <Label htmlFor="routine-title" className="font-medium text-sm">
              Name
            </Label>
            <XStack className="items-center gap-2">
              <IconPickerTrigger
                icon={currentIcon}
                label="Choose routine icon"
                onChange={(nextIcon) =>
                  onDraftChange({
                    icon: nextIcon,
                    iconSuggestionsEnabled: false,
                  })
                }
                onOpenChange={onIconPickerOpenChange}
                onDismissSuggestion={
                  showIconSuggestionDismiss ? dismissIconSuggestion : undefined
                }
              />
              <Input
                id="routine-title"
                type="text"
                placeholder="Weekly work review"
                autoFocus
                value={title}
                onChange={(e) => onDraftChange({ title: e.target.value })}
                maxLength={255}
                error={titleError}
              />
            </XStack>
          </YStack>

          {instructionsField}

          {routine && <RoutineMemorySection routineId={routine.referenceId} />}

          <YStack className="gap-2">
            <p className="text-sm text-foreground-muted">Run</p>
            <YStack className="gap-4 rounded-xl border border-border bg-surface px-4 py-4">
              <RoutineTriggerEditor
                hasTrigger={hasScheduleTrigger}
                advancedSchedulesEnabled={advancedSchedulesEnabled}
                triggers={triggers}
                triggersEnabled={triggersEnabled}
                gmailAccounts={gmailAccounts}
                outlookAccounts={outlookAccounts}
                onConfigureEmailTrigger={onConfigureEmailTrigger}
                emailConfigurationDisabled={
                  isSubmitting || actions?.permissions.canImprove === false
                }
                emailEditingDisabled={
                  isSubmitting ||
                  actions?.permissions.canEditDefinition === false
                }
                recurrenceType={recurrenceType}
                triggerTime={triggerTime}
                hourlyEndTime={hourlyEndTime}
                advancedMonths={advancedMonths}
                advancedDayMode={advancedDayMode}
                advancedMonthDays={advancedMonthDays}
                advancedTimes={advancedTimes}
                hasCustomSchedule={hasCustomSchedule}
                weekDays={weekDays}
                monthDay={monthDay}
                intervalCount={intervalCount}
                intervalUnit={intervalUnit}
                intervalEndAt={intervalEndAt}
                onAdd={onAddScheduleTrigger}
                onRemove={onRemoveScheduleTrigger}
                onIntervalChange={(value) =>
                  onDraftChange({ ...value, preservedRecurrenceRule: null })
                }
                onTriggersChange={(value) => onDraftChange({ triggers: value })}
                webhookEndpoints={webhookEndpoints}
                onCreateWebhookEndpoint={onCreateWebhookEndpoint}
                onManageWebhookEndpoints={onManageWebhookEndpoints}
                onRecurrenceTypeChange={(value) =>
                  onDraftChange({
                    recurrenceType: value,
                    preservedRecurrenceRule: null,
                  })
                }
                onTimeChange={(value) =>
                  onDraftChange({
                    triggerTime: value,
                    preservedRecurrenceRule: null,
                  })
                }
                onHourlyEndTimeChange={(value) =>
                  onDraftChange({
                    hourlyEndTime: value,
                    preservedRecurrenceRule: null,
                  })
                }
                onAdvancedMonthsChange={(value) =>
                  onDraftChange({
                    advancedMonths: value,
                    preservedRecurrenceRule: null,
                  })
                }
                onAdvancedDayModeChange={(value) =>
                  onDraftChange({
                    advancedDayMode: value,
                    preservedRecurrenceRule: null,
                  })
                }
                onAdvancedMonthDaysChange={(value) =>
                  onDraftChange({
                    advancedMonthDays: value,
                    preservedRecurrenceRule: null,
                  })
                }
                onAdvancedTimesChange={(value) =>
                  onDraftChange({
                    advancedTimes: value,
                    preservedRecurrenceRule: null,
                  })
                }
                onWeekDaysChange={(value) =>
                  onDraftChange({
                    weekDays: value,
                    preservedRecurrenceRule: null,
                  })
                }
                onMonthDayChange={(value) =>
                  onDraftChange({
                    monthDay: value,
                    preservedRecurrenceRule: null,
                  })
                }
                error={scheduleError}
              />
              <XStack className="items-center justify-between gap-3">
                <YStack className="min-w-0 gap-0.5">
                  <Label className="font-medium text-sm">Mode</Label>
                  <p className="text-xs text-foreground-muted">
                    {agentModeLocked
                      ? 'Littlebird set the mode for this routine'
                      : 'Sets the speed and depth for each run'}
                  </p>
                </YStack>
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger asChild disabled={agentModeLocked}>
                    <Button variant="outline" size="sm" className="gap-1.5">
                      {
                        AGENT_MODE_OPTIONS.find(
                          (option) => option.value === agentMode
                        )?.label
                      }
                      <Icons.chevronDown className="size-3.5" />
                    </Button>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content align="end" className="min-w-40">
                    <DropdownMenu.RadioGroup
                      value={agentMode}
                      onValueChange={(value) =>
                        onDraftChange({ agentMode: value as RoutineAgentMode })
                      }
                    >
                      {AGENT_MODE_OPTIONS.filter((option) =>
                        option.value === 'god'
                          ? godEnabled
                          : !teamAllowedModes ||
                            teamAllowedModes.includes(option.value)
                      ).map((option) => (
                        <DropdownMenu.RadioItem
                          key={option.value}
                          value={option.value}
                          disabled={
                            (option.value === 'max' && !maxEnabled) ||
                            (option.value === 'ultra' && !ultraEnabled)
                          }
                        >
                          {option.label}
                        </DropdownMenu.RadioItem>
                      ))}
                    </DropdownMenu.RadioGroup>
                  </DropdownMenu.Content>
                </DropdownMenu.Root>
              </XStack>
            </YStack>
          </YStack>

          <YStack className="gap-5">
            <YStack className="gap-2">
              <p className="text-sm text-foreground-muted">Notifications</p>
              <YStack className="gap-4 rounded-xl border border-border bg-surface px-4 py-4">
                <XStack className="items-center justify-between gap-3">
                  <YStack className="min-w-0 gap-0.5">
                    <Label
                      htmlFor="notifications-toggle"
                      className="font-medium text-sm"
                    >
                      Push
                    </Label>
                    <p className="text-xs text-foreground-muted">
                      Send a push notification when a report is ready
                    </p>
                  </YStack>
                  <Switch
                    id="notifications-toggle"
                    disabled={deliveryLocked}
                    checked={notificationsEnabled}
                    onCheckedChange={(value) =>
                      onDraftChange({ notificationsEnabled: value })
                    }
                  />
                </XStack>
                <XStack className="items-center justify-between gap-3">
                  <YStack className="min-w-0 gap-0.5">
                    <Label
                      htmlFor="email-notifications-toggle"
                      className="font-medium text-sm"
                    >
                      Email
                    </Label>
                    <p className="text-xs text-foreground-muted">
                      Send an email when a report is ready
                    </p>
                  </YStack>
                  <Switch
                    id="email-notifications-toggle"
                    disabled={deliveryLocked}
                    checked={emailNotificationsEnabled}
                    onCheckedChange={(value) =>
                      onDraftChange({ emailNotificationsEnabled: value })
                    }
                  />
                </XStack>
                {reachOutAvailable && (
                  <XStack className="items-center justify-between gap-3">
                    <YStack className="min-w-0 gap-0.5">
                      <Label
                        htmlFor="may-reach-out-toggle"
                        className="font-medium text-sm"
                      >
                        May reach out to me when it needs input
                      </Label>
                      <p className="text-xs text-foreground-muted">
                        A run can ask you something or wait for your approval
                        before it finishes
                      </p>
                    </YStack>
                    <Switch
                      id="may-reach-out-toggle"
                      disabled={deliveryLocked}
                      checked={mayReachOut}
                      onCheckedChange={(value) =>
                        onDraftChange({ mayReachOut: value })
                      }
                    />
                  </XStack>
                )}
                {deliveryLocked ? null : (
                  <DeliveryChannelSelect
                    deliveryChannel={deliveryChannel}
                    setDeliveryChannel={(value) =>
                      onDraftChange({ deliveryChannel: value })
                    }
                  />
                )}
                {!deliveryLocked &&
                  deliveryChannel ===
                    MessagingChannelTypes.WHATSAPP_BUSINESS && (
                    <p className="text-xs text-foreground-muted">
                      <strong>Note:</strong> {WHATSAPP_REPLY_WINDOW_NOTE}
                    </p>
                  )}
              </YStack>
            </YStack>
            <YStack className="gap-2">
              <p className="text-sm text-foreground-muted">Controls</p>
              <YStack className="gap-4 rounded-xl border border-border bg-surface px-4 py-4">
                <XStack className="items-center justify-between gap-3">
                  <YStack className="min-w-0 gap-0.5">
                    <Label
                      htmlFor="auto-pause-toggle"
                      className="font-medium text-sm"
                    >
                      Auto-pause
                    </Label>
                    <p
                      id="auto-pause-description"
                      className="text-xs text-foreground-muted"
                    >
                      {channelDeliveryEnabled
                        ? 'Auto-pause applies if Messages delivery becomes unavailable'
                        : 'Pauses when reports go unread'}
                    </p>
                  </YStack>
                  <Switch
                    id="auto-pause-toggle"
                    aria-describedby="auto-pause-description"
                    checked={autoPauseEnabled}
                    disabled={channelDeliveryEnabled || deliveryLocked}
                    onCheckedChange={(value) =>
                      onDraftChange({ autoPauseEnabled: value })
                    }
                  />
                </XStack>
                {!autoPauseEnabled && (
                  <CalloutBox
                    variant="warning"
                    title="This routine will keep running"
                    description="A never-pausing routine can burn through your usage fast"
                  />
                )}
                {routine && (
                  <RoutineCreditShareRow
                    routineId={routine.referenceId}
                    isQuickAgent={agentMode === 'lite'}
                  />
                )}
                {routine &&
                  subscriptionStore.isTeamMember &&
                  actions?.canShare && (
                    <RoutineTeamShareRow routine={routine} />
                  )}
              </YStack>
            </YStack>
            {isEditMode &&
              routine &&
              (canTogglePause || actions?.canDelete) && (
                <YStack className="gap-2">
                  <p className="text-sm text-foreground-muted">Actions</p>
                  <YStack className="gap-4 rounded-xl border border-border bg-surface px-4 py-4">
                    {canTogglePause && (
                      <XStack className="items-center justify-between gap-3">
                        <YStack className="min-w-0 gap-0.5">
                          <Label className="font-medium text-sm">
                            {routine.isPaused ? 'Resume' : 'Pause'}
                          </Label>
                          <p className="text-xs text-foreground-muted">
                            {routine.isPaused
                              ? 'Allow this routine to run again'
                              : 'Stop future runs until you resume this routine'}
                          </p>
                        </YStack>
                        <Button
                          variant="outline"
                          size="sm"
                          className="shrink-0"
                          disabled={pauseActionPending || isSubmitting}
                          onClick={onPauseToggle}
                        >
                          {routine.isPaused ? 'Resume' : 'Pause'}
                        </Button>
                      </XStack>
                    )}
                    {actions?.canDelete && (
                      <XStack className="items-center justify-between gap-3">
                        <YStack className="min-w-0 gap-0.5">
                          <Label className="font-medium text-sm">Delete</Label>
                          <p className="text-xs text-foreground-muted">
                            This cannot be undone
                          </p>
                        </YStack>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="shrink-0"
                          disabled={isSubmitting}
                          onClick={onDelete}
                        >
                          Delete
                        </Button>
                      </XStack>
                    )}
                  </YStack>
                </YStack>
              )}
          </YStack>
        </YStack>
      </div>
    </YStack>
  );
});
